import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { logout } from "../actions/authenActions";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export const fetchTableOne = createAsyncThunk(
  "reviewFrame/fetchTableOne",
  async ({ liverun, license_key }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/ready_count_by_week`,
        { liverun, license_key },
      );
      return res.data || {};
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchTableTwo = createAsyncThunk(
  "reviewFrame/fetchTableTwo",
  async (
    { start_date, end_date, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/ready_count_category_by_week`,
        { start_date, end_date, liverun, license_key },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchTableThree = createAsyncThunk(
  "reviewFrame/fetchTableThree",
  async (
    { start_date, end_date, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/ready_count_by_week_data`,
        { start_date, end_date, liverun, license_key },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchLabReport = createAsyncThunk(
  "reviewFrame/fetchLabReport",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/ready_count_lab_report`,
        { lab_report_id, liverun, license_key },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const saveLabReportActions = createAsyncThunk(
  "reviewFrame/saveLabReportActions",
  async (
    { lab_report_id, actions, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/save_lab_report_actions`,
        { lab_report_id, actions, liverun, license_key },
      );
      return res.data;
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const loadLabReportActions = createAsyncThunk(
  "reviewFrame/loadLabReportActions",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/load_lab_report_actions`,
        { lab_report_id, liverun, license_key },
      );
      return res.data.actions || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

// NEW: Approve lab report (move from False to True)
export const approveLabReport = createAsyncThunk(
  "reviewFrame/approveLabReport",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/approve_lab_report`,
        { lab_report_id, liverun, license_key },
      );
      return { lab_report_id, categories: res.data.categories || [] };
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

// NEW: Unapprove lab report (move from True to False)
export const unapproveLabReport = createAsyncThunk(
  "reviewFrame/unapproveLabReport",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/unapprove_lab_report`,
        { lab_report_id, liverun, license_key },
      );
      return { lab_report_id, categories: res.data.categories || [] };
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

// === SLICE ===

const reviewFrameSlice = createSlice({
  name: "reviewFrame",
  initialState: {
    liverun: true,
    tableOne: [],
    tableOneTotalProcessed: 0,
    tableTwo: [],
    tableThree: [],
    lab: {},
    selectedDate: null,
    selectedLabReport: null,
    loadingOne: false,
    loadingTwo: false,
    loadingThree: false,
    loadingLabReport: false,
    labReportActions: [],
    loadingActions: false,
    savingActions: false,
    updatingApproval: false,
  },
  reducers: {
    setSelectedDate(state, action) {
      state.selectedDate = action.payload;
    },
    setLabReport(state, action) {
      state.selectedLabReport = action.payload;
    },
    setLabReportActions(state, action) {
      state.labReportActions = action.payload;
    },
    clearLabReportActions(state) {
      state.labReportActions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Table One
      .addCase(fetchTableOne.pending, (state) => {
        state.loadingOne = true;
      })
      .addCase(fetchTableOne.fulfilled, (state, action) => {
        const { count_by_week = [], total_processed = 0 } = action.payload;

        state.tableOne = count_by_week.map((rec, idx) => ({
          id: idx,
          date: rec[0],
          start_date: rec[1],
          end_date: rec[2],
          approved: rec[3],
          not_approved: rec[4],
        }));

        state.tableOneTotalProcessed = total_processed;
        state.loadingOne = false;
      })

      // Table Two
      .addCase(fetchTableTwo.pending, (state) => {
        state.loadingTwo = true;
      })
      .addCase(fetchTableTwo.fulfilled, (state, action) => {
        state.tableTwo = action.payload.map((rec, idx) => ({
          id: idx,
          category: rec[0],
          approved: rec[1],
          not_approved: rec[2],
        }));
        state.loadingTwo = false;
      })

      // Table Three
      .addCase(fetchTableThree.pending, (state) => {
        state.loadingThree = true;
      })
      .addCase(fetchTableThree.fulfilled, (state, action) => {
        state.tableThree = action.payload.map((rec, idx) => ({
          id: idx + 1,
          updated_at: rec.updated_at || "",
          emis_id: rec.emis_id || "",
          approved: rec.approved || false,
          lab_report_id: rec.lab_report_id || "",
          categories: Array.isArray(rec.categories) ? rec.categories : [],
        }));
        state.loadingThree = false;
      })

      // Lab Report
      .addCase(fetchLabReport.pending, (state) => {
        state.loadingLabReport = true;
      })
      .addCase(fetchLabReport.fulfilled, (state, action) => {
        const report = action.payload;

        state.lab = {
          license_email: report.license_email || "",
          approved: report.approved ?? null,
          updated_at: report.updated_at || "",
          updated_by: report.updated_by || "",
          patient_data: report.patient_data || {},
          patient_profile: report.patient_profile || {},
          categories: Array.isArray(report.categories) ? report.categories : [],
          actions: Array.isArray(report.actions) ? report.actions : [],
        };

        state.labReportActions = report.actions || [];
        state.loadingLabReport = false;
      })
      .addCase(fetchLabReport.rejected, (state) => {
        state.loadingLabReport = false;
      })

      // Save Lab Report Actions
      .addCase(saveLabReportActions.pending, (state) => {
        state.savingActions = true;
      })
      .addCase(saveLabReportActions.fulfilled, (state, action) => {
        state.savingActions = false;
        if (action.payload.actions) {
          state.labReportActions = action.payload.actions;
        }
      })
      .addCase(saveLabReportActions.rejected, (state) => {
        state.savingActions = false;
      })

      // Approve Lab Report
      .addCase(approveLabReport.pending, (state) => {
        state.updatingApproval = true;
      })
      .addCase(approveLabReport.fulfilled, (state, action) => {
        const { lab_report_id, categories } = action.payload;

        // Update lab.approved
        state.lab.approved = true;

        state.tableThree = state.tableThree.map((report) =>
          report.lab_report_id === lab_report_id
            ? { ...report, approved: true }
            : report,
        );

        // Update tableOne - move count from not_approved to approved
        const selectedDateRecord = state.tableOne.find(
          (r) => r.date === state.selectedDate,
        );
        if (selectedDateRecord) {
          selectedDateRecord.approved += 1;
          selectedDateRecord.not_approved = Math.max(
            0,
            selectedDateRecord.not_approved - 1,
          );
        }

        // Update total processed (shouldn't change but keep consistent)
        // state.tableOneTotalProcessed remains the same

        // Update tableTwo - adjust category counts
        categories.forEach((cat) => {
          const categoryRecord = state.tableTwo.find((r) => r.category === cat);
          if (categoryRecord) {
            categoryRecord.approved += 1;
            categoryRecord.not_approved = Math.max(
              0,
              categoryRecord.not_approved - 1,
            );
          }
        });

        state.updatingApproval = false;
      })
      .addCase(approveLabReport.rejected, (state) => {
        state.updatingApproval = false;
      })

      // Unapprove Lab Report
      .addCase(unapproveLabReport.pending, (state) => {
        state.updatingApproval = true;
      })
      .addCase(unapproveLabReport.fulfilled, (state, action) => {
        const { lab_report_id, categories } = action.payload;

        // Update lab.approved
        state.lab.approved = false;

        // Update tableThree - remove from current list

        state.tableThree = state.tableThree.map((report) =>
          report.lab_report_id === lab_report_id
            ? { ...report, approved: false }
            : report,
        );

        // Update tableOne - move count from approved to not_approved
        const selectedDateRecord = state.tableOne.find(
          (r) => r.date === state.selectedDate,
        );
        if (selectedDateRecord) {
          selectedDateRecord.not_approved += 1;
          selectedDateRecord.approved = Math.max(
            0,
            selectedDateRecord.approved - 1,
          );
        }

        // Update tableTwo - adjust category counts
        categories.forEach((cat) => {
          const categoryRecord = state.tableTwo.find((r) => r.category === cat);
          if (categoryRecord) {
            categoryRecord.not_approved += 1;
            categoryRecord.approved = Math.max(0, categoryRecord.approved - 1);
          }
        });

        state.updatingApproval = false;
      })
      .addCase(unapproveLabReport.rejected, (state) => {
        state.updatingApproval = false;
      });
  },
});

export const {
  setSelectedDate,
  setLabReport,
  setLabReportActions,
  clearLabReportActions,
} = reviewFrameSlice.actions;

export default reviewFrameSlice.reducer;
